import { FC, useEffect, useMemo, useState } from 'react'
import Swal from 'sweetalert2'
import { StatusLoteModalProps } from '../type'
import { useProduct } from '../factory/useProduct'
import { i18n } from '../../../../../../translate/i18n'
import { DELETE, POST } from '../../../../../../services/api'
import { ModularDataTable, TableSelection } from '../../../../../../components/ModularDataTable'
import { createColumns } from '../factory/generateColumns'
import usePluginsSpace from '../../../../../../hooks/usePluginSpace'
import { FilterOptions } from '../components/filterOptions'
import useAuth from '../../../../../../hooks/useAuth'

export const DeleteLoteModal: FC<StatusLoteModalProps> = ({
  isOpen,
  onClose,
}) => {
  const { pluginSpace } = usePluginsSpace()
  const { loginInfo } = useAuth()
  const isWholeSaler = Boolean(pluginSpace?.wholeSaler)

  const [items, setItems] = useState<any[]>([])
  const [selectedProducts, setSelectedProducts] = useState<TableSelection>({ selectedRows: [], selectAll: false })

  const {
    tableLoading, productFilters, loading, pagination, sorting, categories, registeredGroups,
    setTableLoading, setLoading, getProducts, handleDataChange, handlePaginationChange,
    handleSortingChange, handleFilterChange, getCategories, getCustomersGroups,
  } = useProduct('deleteLoteModal', 'deleteLoteModal')

  async function filterProduct() {
    setTableLoading(true)
    const itensRes = await getProducts()
    setItems(itensRes)
    setTableLoading(false)
  }

  function switchStatus(params: { endpoint: string; Id: number; status: boolean }) {
    const { Id, status } = params
    const newItems = items.map(item => {
      if (item.id === Id) {
        return { ...item, status }
      }
      return item
    })
    setItems(newItems)
  }

  const columns = useMemo(
    () => createColumns({ switchStatus, isWholeSaler }),
    []
  )

  async function loadPage() {
    setLoading(true)
    await getCategories()
    if (isWholeSaler) {
      await getCustomersGroups()
    }
    const itensRes = await getProducts()
    setItems(itensRes)
    setLoading(false)
  }

  useEffect(() => {
    if (isOpen) {
      loadPage()
    } else {
      setItems([])
      setSelectedProducts({ selectedRows: [], selectAll: false })
    }
  }, [isOpen])

  const handleDeleteSelecteds = async () => {
    try {
      if (!loginInfo?.pluginspaceId) {
        Swal.fire({
          icon: 'error',
          title: 'Pluginspace invalido',
          text: 'Nao foi possivel identificar o pluginspace para exclusao em lote.',
        })
        return
      }

      if (selectedProducts.selectedRows.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Nenhum item selecionado',
          text: 'Selecione pelo menos um item para excluir!',
        })
        return
      }

      const itemsToDelete = selectedProducts.selectedRows
        .map(index => items[index])
        .map(item => Number(item?.id))
        .filter(id => Number.isFinite(id) && id > 0)
        .map(id => ({ id }))

      if (itemsToDelete.length === 0) {
        Swal.fire({
          icon: 'warning',
          title: 'Nenhum item valido',
          text: 'Nao foi possivel montar a lista de produtos para exclusao.',
        })
        return
      }

      const confirm = await Swal.fire({
        icon: 'warning',
        title: 'Atenção!',
        html: `
          Cuidado ao realizar a exclusão, os dados não poderão ser <b>restaurados!</b>
          <div style="margin-top: 8px">Digite <b>excluir</b> para confirmar!</div>
        `,
        showCloseButton: true,
        showConfirmButton: true,
        confirmButtonColor: '#dc3741',
        confirmButtonText: 'Excluir',
        showCancelButton: false,
        input: 'text',
        inputPlaceholder: 'excluir',
      })

      if (!confirm.isConfirmed) {
        Swal.close()
        return
      }

      if (!confirm.value || String(confirm.value).trim().toLowerCase() !== 'excluir') {
        await Swal.fire({
          icon: 'error',
          title: 'Exclusão não confirmada',
          text: 'Não foi possível excluir por falta de confirmação correta.',
        })
        return
      }

      setLoading(true)
      let response: any = null

      try {
        response = await POST('/product/delete-lote', itemsToDelete)
      } catch (requestError: any) {
        const rawMessage =
          requestError?.response?.data?.message || requestError?.message || ''
        const message = Array.isArray(rawMessage) ? rawMessage.join(' ') : String(rawMessage)

        // Fallback para ambientes onde a rota nova ainda nao foi publicada
        if (message.includes('Cannot POST /api/product/delete-lote')) {
          let deletedCount = 0

          for (const item of itemsToDelete) {
            try {
              const deleteRes = await DELETE(`/product/${item.id}`)
              if (deleteRes?.success) {
                deletedCount += 1
              }
            } catch (deleteError) {
              console.error('Erro ao excluir item em fallback:', deleteError)
            }
          }

          if (deletedCount === 0) {
            throw new Error('Nenhum produto valido encontrado para exclusao neste pluginspace.')
          }

          response = {
            success: true,
            message: `${deletedCount} produto(s) excluido(s) com sucesso`,
          }
        } else {
          throw requestError
        }
      } finally {
        setLoading(false)
      }

      if (response.success) {
        Swal.fire({
          icon: 'success',
          title: 'Sucesso!',
          text: response.message || 'Produtos excluidos em lote com sucesso!',
        }).then(async () => {
          setSelectedProducts({ selectedRows: [], selectAll: false })
          const itensRes = await getProducts()
          setItems(itensRes)
        })
      } else {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response.message || 'Erro ao excluir produtos em lote!',
        })
      }
    } catch (error: any) {
      setLoading(false)
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: error?.message || 'Erro ao excluir produtos em lote!',
      })
    }
  }

  async function newhandleSortingChange(newSorting: any) {
    try {
      const res = await handleSortingChange(newSorting)
      setItems(res)
    } catch (error) {
      console.error('Erro ao mudar ordenação:', error)
    }
  }

  async function newHandlePaginationChange(newPagination: any) {
    try {
      const res = await handlePaginationChange(newPagination)
      setItems(res)
    } catch (error) {
      console.error('Erro ao mudar paginação:', error)
    }
  }

  async function newHandleDataChange(params: any) {
    try {
      const res = await handleDataChange(params)
      setItems(res)
    } catch (error) {
      console.error('Erro ao mudar dados:', error)
    }
  }


  if (!isOpen) return null

  return (
    <div className='modal fade show d-block' style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}>
      <div className='modal-dialog modal-xl'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>Deletar Produto em Lote</h5>
            <button
              type='button'
              className='btn-close'
              onClick={onClose}
              disabled={loading}
            ></button>
          </div>
          <div className='modal-body'>
            <div className='mb-3'>
              <FilterOptions
                productFilters={productFilters}
                handleFilterChange={handleFilterChange}
                filterProduct={filterProduct}
                categories={categories}
                registeredGroups={registeredGroups}
              />
            </div>

            <div className='table-responsive' style={{ maxHeight: '550px', overflow: 'auto' }}>
              <ModularDataTable
                data={items}
                columns={columns}
                pagination={pagination}
                onPaginationChange={newHandlePaginationChange}
                sorting={sorting}
                onSortingChange={newhandleSortingChange}
                onDataChange={newHandleDataChange}
                searchable={true}
                searchPlaceholder='Pesquisar produtos...'
                loading={tableLoading}
                onSelectionChange={setSelectedProducts}
                selection={selectedProducts}
                selectable={true}
                striped={false}
                hover={false}
                size='md'
                className='table-row-bordered table-row-gray-200'
              />
            </div>

            <div className='alert alert-warning mt-3'>
              <i className='bi bi-exclamation-triangle me-2'></i>
              <strong>Atencao:</strong> Esta operacao realiza exclusao logica dos produtos selecionados do pluginspace atual.
            </div>
          </div>
          <div className='modal-footer'>
            <button
              type='button'
              className='btn btn-light'
              onClick={onClose}
              disabled={loading}
            >
              {i18n.t('buttons.cancel')}
            </button>
            <button
              type='button'
              className='btn btn-danger'
              onClick={handleDeleteSelecteds}
              disabled={loading || selectedProducts.selectedRows.length === 0}
            >
              {loading ? (
                <>
                  <span className='spinner-border spinner-border-sm me-2' role='status'></span>
                  Excluindo...
                </>
              ) : (
                'Deletar selecionados'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
